<!-- eslint-disable max-len -->
<template>
  <div class="room-list">
    <booking-header-details @edit="$emit('edit-query', $event)" />
    <div
      v-if="isMultiRoomBookingEnabled"
      class="multibooking-sidebar-wrapper"
    >
      <transition name="slide-right">
        <multi-booking-sidebar
          v-if="isActive"
          ref="multiBookingSidebar"
        />
      </transition>
      <transition name="fade">
        <div
          v-if="isActive"
          class="backlight"
          @click="closeSidebar"
        />
      </transition>
    </div>
    <simple-list
      ref="list"
      :server-items="roomsAvailable"
      class=""
      variant="booking-room-list"
      query="roomType"
      :undefined-value="tc('booking.all')"
    >
      <template #errors>
        <div v-if="lastQuery && !loadingRooms && errorTypeCode">
          <template v-if="errorTypeCode === 'noResultsError'">
            <div
              key="no-results-error"
              class="error-box"
              role="alert"
            >
              <span>
                {{
                  tc("errors.checkAvailability.noAvailabilityError.firstLine")
                }}
              </span>
              <span
                class="no-icon"
                v-html="noAvailabilityErrorSecondLine"
              />
              <span class="no-icon">
                {{
                  tc("errors.checkAvailability.noAvailabilityError.thirdLine")
                }}
              </span>
            </div>
          </template>
          <template v-else>
            <div
              key="general-error"
              class="error-box"
              role="alert"
            >
              <span>
                {{ tc("errors.checkAvailability.genericError.firstLine") }}
              </span>
              <span
                class="no-icon"
                v-html="genericErrorSecondLine"
              />
            </div>
          </template>
        </div>
        <div v-if="areDatesAndGuestsEmpty && !loadingRooms">
          <div
            key="no-date-error"
            class="error-box no-date"
            role="alert"
          >
            <span>
              {{
                tc("errors.dateMissingError.firstLine")
              }}
            </span>
            <span class="no-icon">
              <a
                href=""
                class="error-cta"
                @click="openCalendar($event)"
              >{{ tc("errors.dateMissingError.ctaLabel") }}</a>
            </span>
          </div>
        </div>
      </template>
      <template #filter="{ filters }">
        <div
          v-if="roomsAvailable.length > 0"
          class="filter"
          data-component="booking-room-filters"
        >
          <accessible-tabs>
            <div
              role="tablist"
              class="filter-items"
            >
              <button
                id="room-filter-all"
                class="filter-item tab-group-button"
                :class="[{ active: !roomTypeFilter }]"
                role="tab"
                :aria-selected="!roomTypeFilter"
                :aria-controls="tc('booking.all')"
                @click="filterRoomType($event)"
                @keydown.right.prevent="filterRoomType($event, roomTypes[0])"
                @keydown.left.prevent="
                  filterRoomType(
                    $event,
                    hasAccessibleRooms
                      ? 'accessible'
                      : roomTypes[roomTypes.length - 1]
                  )
                "
              >
                {{ tc("booking.all") }}
              </button>

              <button
                v-for="(roomType, i) in roomTypes"
                :id="'room-filter-' + i"
                :key="roomType"
                class="filter-item tab-group-button"
                :class="[
                  {
                    active: roomTypeFilter === roomType,
                    disabled: roomTypeIsDisabled(roomType),
                  },
                ]"
                role="tab"
                :aria-selected="roomTypeFilter === roomType"
                :aria-controls="roomType"
                :tabindex="roomTypeFilter === roomType && !roomTypeIsDisabled(roomType) ? '0' : '-1'"
                @click="filterRoomType($event, roomType)"
                @keydown.right.prevent="
                  filterRoomType(
                    $event,
                    i === roomTypes.length - 1
                      ? hasAccessibleRooms
                        ? 'accessible'
                        : null
                      : roomTypes[i + 1]
                  )
                "
                @keydown.left.prevent="
                  filterRoomType($event, i === 0 ? null : roomTypes[i - 1])
                "
              >
                {{ roomType }}
              </button>

              <button
                v-if="hasAccessibleRooms"
                id="room-filter-accessible"
                class="filter-item tab-group-button"
                :class="[
                  {
                    active: roomTypeFilter === 'accessible',
                    disabled: roomTypeIsDisabled('accessible'),
                  },
                ]"
                role="tab"
                :aria-selected="roomTypeFilter === 'accessible'"
                :tabindex="
                  roomTypeFilter === 'accessible' && !roomTypeIsDisabled('accessible')
                    ? '0'
                    : '-1'
                "
                @click="filterRoomType($event, 'accessible')"
                @keydown.right.prevent="filterRoomType($event, null)"
                @keydown.left.prevent="
                  filterRoomType($event, roomTypes[roomTypes.length - 1])
                "
              >
                {{ tc("accessibility.accessibility") }}
              </button>
            </div>
          </accessible-tabs>
          <room-filter
            v-if="showRoomFilter"
            ref="roomFilter"
            :rooms="roomsAvailable"
            :room-type-filter="filters"
            :has-accessible-rooms="hasAccessibleRooms"
            :accessible-selected="roomTypeFilter === 'accessible'"
            @updateFilteredRooms="updateRoomsAvailable"
          >
            <button
              slot-scope="{
                activate,
                isActive,
                resetAllFilters,
                totalFiltersSelected,
              }"
              class="room-filter--activator"
              :class="totalFiltersSelected ? 'filter-selected' : ''"
              @click="activate"
            >
              <span
                class="room-filter--activator-label"
                :class="totalFiltersSelected ? 'filter-selected' : ''"
              >{{ tc("booking.filter") }}</span>
              <span
                v-if="totalFiltersSelected > 0"
                class="room-filter--activator-total-tag"
              >{{ totalFiltersSelected }}</span>
              <span class="icon-filter-black" />
              <button
                v-if="totalFiltersSelected > 0"
                :tabindex="isActive ? '-1' : '0'"
                class="room-filter--activator-reset"
                @click.stop="resetAllFilters"
              >
                {{ tc("booking.reset") }}
              </button>
            </button>
          </room-filter>
        </div>
      </template>
      <template #banner>
        <div v-if="isBookingEditMode && !loadingRooms && bookingReady">
          <modify-edit-banner :is-room-list="true" />
        </div>
      </template>
      <template #no-items>
        <div
          v-if="lastQuery && bookingReady"
          key="general-error"
          class="error-box no-margin"
          role="alert"
        >
          <span>
            {{ tc("errors.checkAvailability.genericError.firstLine") }}
            <button
              v-if="$refs.roomFilter"
              class="room-filter--activator-reset no-margin"
              @click.stop="$refs.roomFilter.resetAllFilters"
            >
              {{ tc("booking.reset") }}
            </button>
          </span>
        </div>
      </template>
      <template #rendered-list="{ items }">
        <ul>
          <li
            v-for="item in items"
            :key="item.id"
          >
            <room-card
              :room="item"
              :filter="roomTypeFilter"
            />
          </li>
        </ul>
      </template>
    </simple-list>
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import Tracking from '@mixins/tracking'
  import sift from 'sift'
  import RoomCard from '../../components/room-card/room-card.vue'
  import BookingHeaderDetails from '../../components/booking-header-details/booking-header-details.vue'
  import multiBookingSidebar from '../../components/multibooking-sidebar/multibooking-sidebar.vue'
  import RoomFilter from '../../../../01.base/room-filter/room-filter.vue'
  import ModifyEditBanner from '../../components/modify-edit-banner/modify-edit-banner.vue'

  export default {
    name: 'RoomList',
    components: {
      RoomCard,
      BookingHeaderDetails,
      multiBookingSidebar,
      RoomFilter,
      ModifyEditBanner,
    },
    mixins: [Tracking],
    beforeRouteEnter(to, from, next) {
      next(async (vm) => {
        if (vm.bookingReady) {
          await vm.updateFooterLabel()
        }
        return true
      })
    },
    props: {
      activate: {
        type: Function,
        required: true,
      },
      showBackBtn: {
        type: Boolean,
        default: true,
      },
    },
    data: () => ({
      isActive: false,
      availableRoomsList: [],
      filteredRooms: null,
    }),
    computed: {
      ...mapState('rooms', ['lastQuery', 'loadingRooms', 'errorTypeCode']),
      ...mapState('booking', {
        query: 'query',
        bookingReady: 'ready',
        step: 'step',
        currentRoomId: 'currentRoomId',
      }),
      ...mapState('cart', ['showMultiBookingSidebar', 'isMultiRoomBookingEnabled']),
      ...mapState('modifyReservation', ['isBookingEditMode']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('rooms', [
        'roomsAvailable',
        'errorType',
        'highlightsFacets',
        'bedTypeFacets',
      ]),
      ...mapGetters('cart', ['itemsInCart']),
      ...mapGetters('modifyReservation', ['getBookingTrackingModifyReservation']),
      ...mapGetters('booking', ['currentRoomIsLastRoom', 'areDatesAndGuestsEmpty', 'bookingFlow']),
      roomTypes() {
        const types = []
        const roomsTypesExisting = this.roomsAvailable.map((r) => r.roomType)
        roomsTypesExisting.forEach((t) => {
          if (t && !types.includes(t)) {
            types.push(t)
          }
        })
        return types
      },
      roomTypeFilter() {
        return this.$route.query.roomType
      },
      hasAccessibleRooms() {
        return this.roomsAvailable
          .map((r) => !!r.bedType.find((b) => b.accessible))
          .find((v) => !!v)
      },
      genericErrorSecondLine() {
        const errorMessageWithCta = this.tc(
          'errors.checkAvailability.genericError.secondLine'
        ).replace(
          '[[ctaLabel]]',
          `<a class="error-cta" href="#">${this.tc(
            'errors.checkAvailability.genericError.ctaLabel'
          )}</a>`
        )
        return errorMessageWithCta
      },
      noAvailabilityErrorSecondLine() {
        const errorMessageWithCta = this.tc(
          'errors.checkAvailability.noAvailabilityError.secondLine'
        ).replace(
          '[[ctaLabel]]',
          `<a class="error-cta" href="#">${this.tc(
            'errors.checkAvailability.noAvailabilityError.ctaLabel'
          )}</a>`
        )
        return errorMessageWithCta
      },
      showRoomFilter() {
        return (
          this.hasAccessibleRooms
          || this.highlightsFacets.length > 0
          || this.bedTypeFacets.length > 0
        )
      },
    },
    watch: {
      bookingReady(val) {
        if (this.bookingReady && val) {
          this.updateFooterLabel()
        }
      },
      roomsAvailable(val) {
        this.availableRoomsList = val
      },
      availableRoomsList: {
        handler(val) {
          this.$refs.list.loadItems({ items: val, total: val.length })
        },
        deep: true,
      },
      loadingRooms(val) {
        if (!val && this.errorTypeCode) {
          this.$nextTick(() => {
            const errorCta = document.querySelector('.error-cta')
            if (errorCta) {
              errorCta.addEventListener('click', (event) => {
                event.preventDefault()
                this.$emit('edit-query', [null, event.target])
              })
            }
          })
        }
      },
      showMultiBookingSidebar(newValue) {
        this.isActive = newValue
      },
      async currentRoomId(newVal, oldVal) {
        const previousRoom = this.getRoomGuests(oldVal)
        const currentRoom = this.getRoomGuests(newVal)
        const guestCountIsEqual = Number(currentRoom.adultGuestCount) === Number(previousRoom.adultGuestCount)
          && Number(currentRoom.childGuestCount) === Number(previousRoom.childGuestCount)

        if (!guestCountIsEqual && !this.loadingRooms && this.bookingReady) {
          await this.getAvailableRooms()
        }
        this.updateFooterLabel()
      },
    },
    async mounted() {
      const query = {}
      if (this.$route.query.roomType) {
        query.roomType = this.$route.query.roomType
      }
      if (query.roomType === 'accessible') {
        this.filterProperties({ 'bedType.accessible': true })
      } else {
        this.filterProperties(query)
      }
      // When the user get back to this page from any step onward
      // and the rooms available are not in the store
      if (
        this.roomsAvailable.length === 0
        && !this.loadingRooms
        && this.bookingReady
      ) {
        this.resetRoomUpgrades()
        await this.getAvailableRooms()
        this.availableRoomsList = this.roomsAvailable
      } else if (this.bookingReady && !this.loadingRooms) {
        if (this.isBookingEditMode) {
          await this.reorderRoomsBySelection()
        }
        this.trackAvailabilityCall()
      }

      this.$nextTick(() => {
        this.$track(this.getTrackingData('booking', this.$route.query, { event: 'step3' }))

        this.$track(this.getTrackingData(this.isBookingEditMode ? 'modifyReservation' : 'booking', this.$route.query, {
          event: 'bookingStateChange',
          bookingEngine: {
            bookingStepNumber: this.isBookingEditMode ? '1.1' : '2.1',
            bookingStepName: 'Rooms & Packages',
            bookingEngineVersion: this.isBookingEditMode ? '2' : '1',
            bookingFlow: this.bookingFlow(this.$route.query)
          }
        }))
      })
    },
    methods: {
      ...mapActions('rooms', [
        'getAvailableRooms',
        'trackAvailabilityCall',
        'reorderRoomsBySelection',
      ]),
      ...mapActions('cart', ['resetRoomUpgrades']),
      ...mapActions('booking', ['updateQuery', 'updateNextStepButton']),

      filterProperties(filter) {
        this.$refs.list.updateFilter(filter)
      },
      filterRoomType(e, type) {
        e.preventDefault()
        const typeIndex = this.roomTypes.indexOf(type)
        if (type && this.$route.query.roomType !== type) {
          this.$router.replace({
            query: {
              ...this.query,
              roomType: type,
            },
          })
          if (type === 'accessible') {
            this.filterProperties({ 'bedType.accessible': true })
            if (!this.$refs.roomFilter?.isOpen) {
              document.querySelector('#room-filter-accessible').focus()
            }
          } else {
            this.filterProperties({ roomType: type })
            if (!this.$refs.roomFilter?.isOpen) {
              document.querySelector(`#room-filter-${typeIndex}`).focus()
            }
          }
        } else if (!type) {
          const newQuery = {
            ...this.query,
          }
          delete newQuery.roomType
          this.$router.replace({ query: newQuery }).catch(() => {})
          this.filterProperties({})
          if (!this.$refs.roomFilter?.isOpen) {
            document.querySelector('#room-filter-all').focus()
          }
        }
      },
      async removeRoomFromQuery() {
        await this.updateQuery({ prop: 'roomCodes', value: [] })
        await this.updateQuery({ prop: 'roomName', value: null })
        await this.updateQuery({ prop: 'bedType', value: null })
        this.resetRoomUpgrades()
        this.getAvailableRooms()
      },
      closeSidebar() {
        this.$refs.multiBookingSidebar.onReject()
      },
      updateRoomsAvailable(filteredRooms) {
        this.availableRoomsList = filteredRooms
      },
      roomTypeIsDisabled(roomType) {
        if (roomType === 'accessible') {
          return (
            this.$refs?.roomFilter
              ?.filteredRoomsByFacets(this.roomsAvailable, false)
              .filter(sift({ 'bedType.accessible': true })).length === 0
          )
        }

        return (
          this.$refs?.roomFilter
            ?.filteredRoomsByFacets(this.roomsAvailable, false)
            .filter(sift({ roomType })).length === 0
        )
      },
      async updateFooterLabel() {
        const isOverviewPage = this.$route.name === 'rooms-overview'
        if (
          this.isBookingEditMode
          && this.currentRoomIsLastRoom
          && this.query.roomQuantity > 1
          && this.step === 0
          && !isOverviewPage
        ) {
          const label = `${this.tc('booking.skipTo')
          } ${
            this.tc('booking.roomOverviewTitle')}`
          await this.updateNextStepButton({
            label,
            step: 'rooms-overview',
            customClass: '',
          })
        } else {
          const label = `${this.tc('booking.skipTo')
          } ${
            this.tc('booking.complimentaryPrivileges')}`
          await this.updateNextStepButton({ label, step: 1, customClass: '' })
        }
      },
      getRoomGuests(roomId) {
        if (roomId === 0) {
          return {
            adultGuestCount: this.query.adultGuestCount,
            childGuestCount: this.query.childGuestCount,
          }
        }
        return this.query.aditionalGuests[roomId - 1]
      },
      openCalendar(e) {
        e.preventDefault()
        if (this.areDatesAndGuestsEmpty) {
          this.$emit('edit-query', ['calendar', this.$el.querySelector('.date')])
        }
      }
    },
  }
</script>
